<script setup lang="ts">
  import { type AppRoute, type GatRouteLocation } from '@gat-ship/lib/router'
  import { type IconValues } from '@gat-ship/icons'
  import { useSidebar } from './sidebar-provider.vue'
  import { computed, inject, ref, watch } from 'vue'
  import { useSidebarMenuCollapsible } from './sidebar-menu-collapsible.vue'
  import { useLink, useRouter } from '@gat-ship/lib/router'
  const props = defineProps<{
    to: GatRouteLocation<AppRoute> | string
    icon?: IconValues
    text: string
    disabled?: boolean
  }>()

  const isChildOfSidebarMenuCollapsible = inject(
    'sidebar-menu-collapsible',
    false
  )

  const { isRail } = useSidebar({
    isRail: computed(() => false),
    toggleRail: () => {}
  })

  const { setIsOpen, setIsActiveRoute } = useSidebarMenuCollapsible({
    isOpen: ref(false),
    setIsOpen: () => {},
    setIsActiveRoute: () => {}
  })

  const { isActive: isActiveLink } = useLink({ to: props.to })

  watch(
    isActiveLink,
    (value) => {
      if (value) {
        setIsOpen(true)
      }
      setIsActiveRoute(value)
    },
    {
      immediate: true
    }
  )

  const router = useRouter()

  function handleClickEvent(event: MouseEvent) {
    if (event.shiftKey === true || event.button === 1) {
      event.preventDefault()
      event.stopPropagation()
      if (typeof props.to !== 'string') {
        // Added to fix an issue with current database id being passed to the new tab
        router.openInNewTab(props.to)
        return
      }
    }
  }
</script>

<template>
  <router-link
    v-if="isRail && !isChildOfSidebarMenuCollapsible"
    :to="to"
    :disabled="disabled"
    class="text-secondary-foreground hover:bg-accent/50 group relative flex w-fit select-none items-center justify-center rounded-sm p-2"
    :class="{
      'is-disabled pointer-events-none opacity-50': disabled
    }"
    active-class="bg-accent/50 is-active"
    exact-active-class="exact-active"
    @auxclick="handleClickEvent"
    @click="handleClickEvent"
  >
    <gat-icon
      v-if="icon"
      :icon="icon"
      class="group-[.is-active]:text-primary size-4.5 pointer-events-none cursor-default"
      :class="{ 'text-soft': isChildOfSidebarMenuCollapsible }"
    />
    <div
      class="bg-primary absolute -left-[15px] top-1/2 hidden h-5 w-1 origin-left -translate-y-1/2 scale-100 rounded-r-full transition duration-200 ease-out group-[.exact-active]:block"
    />
  </router-link>
  <router-link
    v-else
    :to="to"
    class="text-secondary-foreground hover:bg-accent/50 group relative flex select-none items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 transition duration-200 ease-out"
    :class="{
      'ml-6': isChildOfSidebarMenuCollapsible && !isRail,
      'w-full': !isChildOfSidebarMenuCollapsible,
      'is-disabled pointer-events-none opacity-50': disabled
    }"
    active-class="bg-accent/50 is-active"
    exact-active-class="exact-active"
    @auxclick="handleClickEvent"
    @click="handleClickEvent"
    ><div
      v-if="!isRail"
      class="bg-primary absolute -left-5 top-1/2 hidden h-5 w-1 origin-left -translate-y-1/2 scale-100 rounded-r-full transition duration-200 ease-out group-[.exact-active]:block"
      :class="{ '-left-11': isChildOfSidebarMenuCollapsible && !isRail }"
    />
    <gat-icon
      v-if="icon"
      :icon="icon"
      class="group-[.is-active]:text-primary size-4"
      :class="{ 'text-soft': isChildOfSidebarMenuCollapsible }"
    />
    <div class="flex grow items-center gap-2" data-hide-collapsed="true">
      <div class="flex-1 text-sm">{{ text }}</div>
      <gat-icon
        :icon="$icons.common.ChevronRight"
        class="hidden size-4 group-[.is-active]:block"
        :class="{
          'size-4': isChildOfSidebarMenuCollapsible
        }"
      />
    </div>
  </router-link>
</template>
